import { Divider, Layout, Text, TextLink } from '@oneaudi/unified-web-components';
import { useI18n } from '~/app/i18n/i18n';
import {
	PackageContentList,
	PackageContentSection,
	PackageContentStyle,
} from '~/app/pages/configurator/components/more-information-dialog/components/package-content/package-content-style';
import { showMoreInformationDialog } from '~/app/pages/configurator/models/show-more-information-dialog';
import { useDebugMode } from '~/app/utils/debug-mode';
import { ConfiguredCarPackageFeatureFragment, ConfiguredCarPackageFragment } from '~/graphql/__generated__/graphql';
import { sanitizeText } from '~/utils/sanitize-text';

export function PackageContent({
	packageData,
	setSubItem,
}: {
	packageData: ConfiguredCarPackageFragment;
	setSubItem?: (item: ConfiguredCarPackageFeatureFragment) => void;
}) {
	const { i18n } = useI18n();

	// According to https://collaboration.msi.audi.com/jira/browse/NWM-264 series items should be hidden
	const nonStandardFeatures = packageData.content?.filter((f) => !f.status.standard) ?? [];

	return (
		<PackageContentSection tabIndex={0} aria-labelledby={'package-content-headline'}>
			<Text
				id={'package-content-headline'}
				margin={[0, 0, '16px', 0]}
				variant={{ type: 'copy', order: '1', style: 'bold' }}>
				{i18n('ngc.dialogs.packageContent')}
			</Text>
			<Divider />
			<PackageContentList>
				{nonStandardFeatures.map((content) => (
					<PackageFeature setSubItem={setSubItem} key={content.contentId} content={content} />
				))}
			</PackageContentList>
		</PackageContentSection>
	);
}

function PackageFeature({
	content,
	setSubItem,
}: {
	content: ConfiguredCarPackageFeatureFragment;
	setSubItem?: (item: ConfiguredCarPackageFeatureFragment) => void;
}) {
	const { getName } = useDebugMode();
	const sanitizedName = sanitizeText(getName({ pr7: content.contentId, ...content })!);
	const showMoreInformation = showMoreInformationDialog(content);

	const { i18n } = useI18n();

	return (
		<>
			<PackageContentStyle key={content.contentId}>
				<Layout
					direction={'column'}
					rowGap={'8px'}
					columnGap={'24px'}
					padding={['16px', '16px', '16px', '16px']}
					className="wrapper">
					{content.images?.teaserImage?.url ? (
						<img src={content.images.teaserImage.url} alt={`video for ${sanitizedName}`} />
					) : (
						<div className="image-not-available" />
					)}

					<Layout direction={'column'} columnGap={'8px'} rowGap={'8px'} justify={'space-between'}>
						<Layout direction={'column'} columnGap={'8px'} rowGap={'8px'}>
							<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
								{/* eslint-disable-next-line */}
								<span tabIndex={0} dangerouslySetInnerHTML={{ __html: sanitizedName }} />
							</Text>
							{/* https://collaboration.msi.audi.com/jira/browse/NWM-150 Currently, we are hiding package content prices, because they are not available. */}
							{/* <ProductInformationText status={feature.status} price={feature.price} rate={feature.rate} /> */}
						</Layout>
						{/* TODO: Currently, it won't be possible to change configuration with "nested" features */}
						{/** I think, it should not be possible anyway, because the price of a nested item is different from the standalone item */}
						{/* TODO: Need to rework changeConfiguration logic to accept ConfiguredCarFeatures / ConfiguredCarPackages instead of ConfiguratorItems */}
						{showMoreInformation && (
							<TextLink
								onClick={() => setSubItem && setSubItem(content)}
								size="sm"
								iconPosition={'trailing'}
								icon={'forward'}
								variant="standalone">
								{i18n('ngc.actions.moreInfo')}
							</TextLink>
						)}
					</Layout>
				</Layout>
				<Divider />
			</PackageContentStyle>
		</>
	);
}
